import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";

const SLIDES = [
  {
    src: "https://images.unsplash.com/photo-1758448756084-c44d73014c07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwZWxlZ2FudCUyMGJyaWdodCUyMGludGVyaW9yfGVufDB8fHx8MTc3ODU5Njk0Mnww&ixlib=rb-4.1.0&q=85",
    label: "Interior · Köln",
  },
  {
    src: "https://images.unsplash.com/photo-1622015663381-d2e05ae91b72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjB2aWxsYSUyMGV4dGVyaW9yfGVufDB8fHx8MTc3ODU5NjkzOHww&ixlib=rb-4.1.0&q=85",
    label: "Architektur · Aachen",
  },
  {
    src: "https://images.unsplash.com/photo-1771033311771-7ec7fa1be3df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxkcm9uZSUyMHNob3QlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZ3xlbnwwfHx8fDE3Nzg1OTY5NDh8MA&ixlib=rb-4.1.0&q=85",
    label: "Drohne · NRW",
  },
  {
    src: "https://images.unsplash.com/photo-1759347137149-2ac7842782cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZWxlZ2FudCUyMGJyaWdodCUyMGludGVyaW9yfGVufDB8fHx8MTc3ODU5Njk0Mnww&ixlib=rb-4.1.0&q=85",
    label: "Wohnen · Düren",
  },
];

export default function Hero() {
  const [active, setActive] = useState(0);
  const [tick, setTick] = useState(0); // bumps to reset interval

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % SLIDES.length), 6000);
    return () => clearInterval(id);
  }, [tick]);

  const goTo = (i) => {
    setActive(i);
    setTick((t) => t + 1);
  };

  return (
    <section
      id="start"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0E0E0E]"
    >
      {/* Slide stack */}
      <div className="absolute inset-0">
        {SLIDES.map((s, i) => (
          <div
            key={s.src}
            data-testid={`hero-slide-${i}`}
            className={`hero-slide absolute inset-0 ${i === active ? "active" : ""}`}
          >
            <img src={s.src} alt={s.label} className="w-full h-full object-cover" />
          </div>
        ))}
        {/* Cinematic dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E0E]/85 via-[#0E0E0E]/55 to-[#0E0E0E]/90"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E0E0E]/40 via-transparent to-[#0E0E0E]/40"></div>
      </div>

      {/* Centered content */}
      <div className="relative container-x py-32 md:py-40 w-full">
        <div className="max-w-4xl mx-auto text-center reveal">
          <div className="mb-8 flex justify-center">
            <span className="overline inline-block bg-[#F5F2EC] text-[#1A1A1A] px-4 py-2 shadow-sm">
              Premium Immobilienfotografie · NRW
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-[56px] sm:text-7xl lg:text-[112px] leading-[1.0] tracking-[-0.015em] text-[#F5F2EC] font-light"
          >
            Ihre Immobilie.
            <br />
            <span className="text-[#FFA500] italic font-normal">Perfekt inszeniert.</span>
          </h1>

          <p className="mt-10 max-w-2xl mx-auto text-[16px] md:text-[19px] leading-[1.65] text-[#D8D5CD] font-light">
            Wertbild verwandelt anspruchsvolle Objekte in visuelle Meisterwerke.
            Für Makler, Bauträger und Eigentümer in NRW, die mehr verlangen als
            nur ein Bild.
          </p>

          <div className="mt-14 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#kontakt"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-between px-8 py-4 bg-[#FFA500] text-[#0E0E0E] text-xs uppercase tracking-[0.2em] hover:bg-[#F5F2EC] transition-colors duration-300 min-w-[260px] font-medium"
            >
              Projekt anfragen
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </a>
            <a
              href="#portfolio"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center px-8 py-4 border border-[#F5F2EC]/40 text-[#F5F2EC] text-xs uppercase tracking-[0.2em] hover:border-[#FFA500] hover:text-[#FFA500] transition-colors duration-300 min-w-[260px]"
            >
              Portfolio ansehen
            </a>
          </div>
        </div>
      </div>

      {/* Slide indicators - lines only, no numbers */}
      <div className="absolute bottom-10 md:bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-4 z-10">
        {SLIDES.map((s, i) => (
          <button
            key={s.src}
            data-testid={`hero-slide-indicator-${i}`}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}: ${s.label}`}
            className="group py-3"
          >
            <span
              className={`block transition-all duration-500 ${
                i === active
                  ? "w-14 h-px bg-[#FFA500]"
                  : "w-8 h-px bg-[#F5F2EC]/30 group-hover:bg-[#F5F2EC]/60"
              }`}
            ></span>
          </button>
        ))}
      </div>
    </section>
  );
}
